import {
  CSSProperties,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { debounce, isEmpty } from 'lodash';
import {
  Column,
  ColumnDef,
  ColumnPinningState,
  ColumnSizingState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  LocalStorageAcceptableKeys,
  LocalStorageAggregationTablesResizeKeys,
} from '@/common/types';
import { useTanTableSorting } from './hooks/useTanTableSorting';
import { useTanTableColumnSizing } from './hooks/useTanTableColumnSizing';

export const TABLE_COLUMN_WIDTHS = {
  MIN: 100,
  HALF: 125,
  '60_PERCENT': 150,
  '70_PERCENT': 175,
  '80_PERCENT': 200,
  '90_PERCENT': 225,
  MAX: 250,
};
export const FARM_TABLE_RESIZE_MULTIPLIER = 10;
const DEFAULT_NO_DATA_MESSAGE = 'No data found.';

export const getPinnedColumnStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : '0px',
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : '0px',

    position: isPinned ? 'sticky' : 'relative',
    zIndex: isPinned ? 1 : 0,
  };
};

export const getLastLeftPinnedColumnStyles = (
  column: Column<any>,
): CSSProperties => {
  const isLastLeftPinnedColumn =
    column.getIsPinned() === 'left' && column.getIsLastColumn('left');

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '5px 0 6px 0px rgba(161, 161, 161, 0.3)'
      : undefined,
  };
};

export type TableBaseProps = {
  columns: ColumnDef<any, any>[];
  data: any[];
  className?: string;
  enableColumnResizing?: boolean;
  columnSizing?: ColumnSizingState;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  enableRowHover?: boolean;
  scrollToSelectedRow?: boolean;
  columnPinning?: ColumnPinningState;
  globalFilter?: string;
  isLoading?: boolean;
  onRowClick?: (row: any) => void;
  onRowSelection?: (row: Row<any>) => void;
  onTableHeightResizedCallback?: () => void;
  setColumnSizing?: (newColumnSizing: ColumnSizingState) => void;
  enableSorting?: boolean;
  localStorageKey?: LocalStorageAcceptableKeys;
  defaultSortingKey?: string;
  defaultSortingDirection?: 'asc' | 'desc';
  testId?: string;
  rowHeight?: number;
  externalRef?: RefObject<HTMLDivElement | null>;
  noDataMessage?: string;
  stickyTopRows?: ReactNode[];
  stickyBottomRows?: ReactNode[];
  localStorageResizeKey?:
    | LocalStorageAcceptableKeys
    | LocalStorageAggregationTablesResizeKeys;
};

import './TanTable.scss';
import TanTableBody from './TanTableBody';
import TanTableHead from './TanTableHead';
import TanTableFooter from './TanTableFooter';

export default function TanTable({
  columns,
  data,
  className,
  enableColumnResizing,
  columnSizing,
  enableRowSelection,
  rowSelection,
  enableRowHover,
  scrollToSelectedRow,
  columnPinning,
  globalFilter,
  isLoading,
  onRowClick,
  onRowSelection,
  onTableHeightResizedCallback,
  setColumnSizing,
  enableSorting = false,
  localStorageKey,
  localStorageResizeKey,
  defaultSortingKey,
  defaultSortingDirection,
  testId = '',
  rowHeight = 28,
  externalRef,
  noDataMessage = DEFAULT_NO_DATA_MESSAGE,
  stickyTopRows = [],
  stickyBottomRows = [],
}: TableBaseProps) {
  const defaultRef = useRef<HTMLDivElement>(null);
  const containerRef = externalRef ?? defaultRef;

  const [isScrolledRight, setIsScrolledRight] = useState(false);
  const { columnSizingState, handleColumnSizingChange } =
    useTanTableColumnSizing({
      localStorageKey: localStorageResizeKey,
      columnSizing,
      setColumnSizing,
    });

  const { sorting, handleSortingChange } = useTanTableSorting({
    columns,
    localStorageKey,
    defaultSortingKey,
    defaultSortingDirection,
  });

  const table = useReactTable({
    data,
    defaultColumn: {
      size: 0, // auto width column until user resizes
      minSize: enableColumnResizing ? 75 : 0,
      maxSize: 600,
      sortingFn: 'alphanumeric',
    },
    columns,
    state: {
      ...(Object.keys(columnSizingState).length > 0 && {
        columnSizing: columnSizingState,
      }),
      ...(globalFilter !== undefined && { globalFilter }),
      ...(columnPinning !== undefined && { columnPinning }),
      ...(rowSelection !== undefined && { rowSelection }),
      ...(enableSorting && { sorting }),
    },
    enableColumnResizing,
    columnResizeMode: 'onChange',
    globalFilterFn: 'includesString',
    enableSorting,
    enableRowSelection,
    enableMultiRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange,
    onColumnSizingChange: handleColumnSizingChange,
    getRowId: (originalRow, index) =>
      originalRow.id ? String(originalRow.id) : String(index),
  });
  const { rows } = table.getSortedRowModel();
  const rowsLengthAfterFilter = table.getFilteredRowModel().rows.length;

  const showNoDataRow =
    (!isLoading && isEmpty(data)) || rowsLengthAfterFilter === 0;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });

  useEffect(() => {
    if (!onTableHeightResizedCallback) return;
    if (!containerRef.current) return;

    const debouncedCallback = debounce(() => {
      onTableHeightResizedCallback();
    }, 1000);

    const observer = new ResizeObserver(debouncedCallback);
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      debouncedCallback.cancel();
    };
  }, [onTableHeightResizedCallback]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current && containerRef.current.scrollLeft > 1) {
        setIsScrolledRight(true);
      } else {
        setIsScrolledRight(false);
      }
    };

    containerRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      containerRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (data && scrollToSelectedRow && rowSelection) {
      const selectedIndex = rows.findIndex(
        (r) => r.original.id === Object.keys(rowSelection)[0],
      );
      const scrollToIndex = Math.max(0, selectedIndex - 10);

      virtualizer.scrollToIndex(scrollToIndex, {
        align: 'start',
      });
    }
  }, [data, scrollToSelectedRow, rowSelection]);

  return (
    <div ref={containerRef} className={`table-container ${className}`}>
      <table
        data-testid={testId}
        className={`${
          enableColumnResizing ? 'tan-table--resizable-table' : ''
        }`}
      >
        <TanTableHead
          table={table}
          localStorageKey={localStorageKey}
          isScrolledRight={isScrolledRight}
          enableColumnResizing={enableColumnResizing}
          stickyTopRows={stickyTopRows}
        />

        <TanTableBody
          rowVirtualizer={virtualizer}
          rows={rows}
          columns={columns}
          noDataMessage={noDataMessage}
          isScrolledRight={isScrolledRight}
          isLoading={isLoading}
          isEmpty={showNoDataRow}
          enableRowHover={enableRowHover}
          onRowClick={onRowClick}
          onRowSelection={onRowSelection}
        />

        {stickyBottomRows && (
          <TanTableFooter stickyBottomRows={stickyBottomRows} />
        )}
      </table>
    </div>
  );
}
